import type {
  Hex,
  TypedDataDomain,
  TypedDataParameter
} from 'viem';
import {
  hashTypedData,
  recoverTypedDataAddress,
  verifyTypedData
} from 'viem';

// Type to derive the message structure from const types
type InferMessageType<TTypes extends Record<string, readonly TypedDataParameter[]>, TPrimaryType extends keyof TTypes> = {
  [K in TTypes[TPrimaryType][number]as K['name']]: K['type'] extends 'string' ? string
  : K['type'] extends 'uint32' | 'uint16' | 'uint8' | 'int32' | 'int16' | 'int8' ? number
  : K['type'] extends 'uint256' | 'int256' ? bigint
  : K['type'] extends 'bool' ? boolean
  : K['type'] extends 'address' ? Hex
  : K['type'] extends `bytes${string}` ? Hex
  : K['type'] extends 'string' ? string
  : unknown;
};

/**
 * EIP712Signer class for generating and verifying EIP-712 typed data signatures
 *
 * This class provides strongly typed methods for:
 * - Creating EIP-712 signature requests
 * - Generating message hashes
 * - Verifying signatures
 * - Recovering signer addresses
 *
 * All methods are strictly typed using generics for maximum type safety.
 */
export class EIP712Signer<
  TTypes extends Record<string, readonly TypedDataParameter[]> = Record<string, readonly TypedDataParameter[]>,
  TPrimaryType extends keyof TTypes = keyof TTypes
> {
  private readonly domain: TypedDataDomain;
  private readonly types: TTypes;
  private readonly primaryType: TPrimaryType;

  /**
   * Creates a new EIP712Signer instance
   *
   * @param domain - The EIP-712 domain specification
   * @param types - The EIP-712 types definition
   * @param primaryType - The primary type for the message
   */
  constructor(domain: TypedDataDomain, types: TTypes, primaryType: TPrimaryType) {
    this.domain = domain;
    this.types = types;
    this.primaryType = primaryType;
  }

  /**
   * Creates a signature request object that can be used with wallet providers
   *
   * @param message - The message data to be signed
   * @returns A complete EIP-712 signature request
   */
  createSignatureRequest<TMessage extends InferMessageType<TTypes, TPrimaryType>>(message: TMessage) {
    return {
      domain: this.domain,
      types: this.types,
      primaryType: this.primaryType,
      message
    } as const;
  }

  /**
   * Generates the message hash for the given message data
   *
   * @param message - The message data to hash
   * @returns The keccak256 hash of the typed data
   */
  generateMessageHash(message: InferMessageType<TTypes, TPrimaryType>): Hex {
    const typedData = {
      domain: this.domain,
      types: this.types,
      primaryType: this.primaryType,
      message
    };
    return hashTypedData(typedData as Parameters<typeof hashTypedData>[0]);
  }

  /**
   * Verifies that a signature was created by the expected signer
   *
   * @param message - The original message data
   * @param signature - The signature to verify
   * @param expectedSigner - The address of the expected signer
   * @returns Promise that resolves to true if the signature is valid and matches the expected signer
   */
  async verifySignature(
    message: InferMessageType<TTypes, TPrimaryType>,
    signature: Hex,
    expectedSigner: Hex
  ): Promise<boolean> {
    try {
      const verifyParams = {
        address: expectedSigner,
        domain: this.domain,
        types: this.types,
        primaryType: this.primaryType,
        message,
        signature
      };
      return await verifyTypedData(verifyParams as Parameters<typeof verifyTypedData>[0]);
    } catch (error) {
      console.log(error)
      return false;
    }
  }

  /**
   * Recovers the signer address from a signature
   *
   * @param message - The original message data
   * @param signature - The signature to recover from
   * @returns Promise that resolves to the address of the signer, or null if recovery fails
   */
  async recoverSigner(signature: Hex, message: InferMessageType<TTypes, TPrimaryType>): Promise<Hex | null> {
    try {
      const recoverParams = {
        domain: this.domain,
        types: this.types,
        primaryType: this.primaryType,
        message,
        signature
      };
      return await recoverTypedDataAddress(recoverParams as Parameters<typeof recoverTypedDataAddress>[0]);
    } catch (error) {
      return null;
    }
  }

  /**
   * Creates a complete signature result object
   *
   * @param message - The original message data
   * @param signature - The generated signature
   * @returns A complete EIP-712 signature result with all metadata
   */
  createSignatureResult(message: InferMessageType<TTypes, TPrimaryType>, signature: Hex) {
    return {
      signature,
      messageHash: this.generateMessageHash(message),
      domain: this.domain,
      types: this.types,
      primaryType: this.primaryType,
      message
    } as const;
  }

  /**
   * Gets the current domain configuration
   *
   * @returns The EIP-712 domain configuration
   */
  getDomain(): TypedDataDomain {
    return { ...this.domain };
  }

  /**
   * Gets the current types configuration
   *
   * @returns The EIP-712 types configuration
   */
  getTypes(): TTypes {
    return { ...this.types };
  }

  /**
   * Gets the primary type
   *
   * @returns The primary type string
   */
  getPrimaryType(): TPrimaryType {
    return this.primaryType;
  }

  /**
   * Creates a new EIP712Signer instance with updated domain
   *
   * @param newDomain - The new domain configuration
   * @returns A new EIP712Signer instance with the updated domain
   */
  withDomain(newDomain: TypedDataDomain): EIP712Signer<TTypes, TPrimaryType> {
    return new EIP712Signer(newDomain, this.types, this.primaryType);
  }

  /**
   * Creates a new EIP712Signer instance with updated types
   *
   * @param newTypes - The new types configuration
   * @param newPrimaryType - The new primary type
   * @returns A new EIP712Signer instance with the updated types
   */
  withTypes<TNewTypes extends Record<string, readonly TypedDataParameter[]>>(
    newTypes: TNewTypes,
    newPrimaryType: keyof TNewTypes
  ): EIP712Signer<TNewTypes, keyof TNewTypes> {
    return new EIP712Signer(this.domain, newTypes, newPrimaryType);
  }

  /**
   * Creates a new EIP712Signer instance with updated message type
   *
   * @param newPrimaryType - The new primary type
   * @returns A new EIP712Signer instance with the updated message type
   */
  withMessageType<TNewPrimaryType extends keyof TTypes>(
    newPrimaryType: TNewPrimaryType
  ): EIP712Signer<TTypes, TNewPrimaryType> {
    return new EIP712Signer(this.domain, this.types, newPrimaryType);
  }
}
